import React, { useCallback, useState } from 'react';
import { cn } from '@utils/cn';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

export interface AccordionProps {
  /** Main label / left-side content of the header. */
  header: React.ReactNode;
  /** Optional right-aligned content (totals, badges, etc.). */
  rightLabel?: React.ReactNode;
  /** Initial expanded state when used uncontrolled. */
  defaultExpanded?: boolean;
  /** Controlled expanded state — pass together with `onToggle`. */
  expanded?: boolean;
  onToggle?: (next: boolean) => void;
  className?: string;
  bodyClassName?: string;
  size?: 'sm' | 'md';
  children: React.ReactNode;
}

export const Accordion: React.FC<AccordionProps> = ({
  header,
  rightLabel,
  defaultExpanded = false,
  expanded: controlled,
  onToggle,
  className,
  bodyClassName,
  size = 'md',
  children,
}) => {
  const [internal, setInternal] = useState(defaultExpanded);
  const isControlled = controlled !== undefined;
  const expanded = isControlled ? controlled : internal;

  const handleToggle = useCallback(() => {
    const next = !expanded;
    if (!isControlled) setInternal(next);
    onToggle?.(next);
  }, [expanded, isControlled, onToggle]);

  const headerPadding = size === 'sm' ? 'px-3 py-2' : 'px-3 py-2.5';

  return (
    <div
      className={cn(
        'rounded-lg ring-1 ring-gray-900/5 bg-white overflow-hidden',
        className,
      )}
    >
      <button
        type="button"
        onClick={handleToggle}
        aria-expanded={expanded}
        className={cn(
          'w-full flex items-center justify-between gap-3 text-left hover:bg-gray-50 transition-colors',
          headerPadding,
        )}
      >
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <KeyboardArrowDownIcon
            sx={{ fontSize: 18 }}
            className={cn(
              'text-gray-500 transition-transform duration-200 flex-shrink-0',
              expanded ? 'rotate-0' : '-rotate-90',
            )}
          />
          <div className="min-w-0 flex-1">{header}</div>
        </div>
        {rightLabel !== undefined && rightLabel !== null && (
          <div className="flex-shrink-0 text-xs font-semibold text-gray-700">
            {rightLabel}
          </div>
        )}
      </button>
      {expanded && (
        <div
          className={cn('px-3 pb-3 pt-2 border-t border-gray-100', bodyClassName)}
        >
          {children}
        </div>
      )}
    </div>
  );
};

export default Accordion;
